<template>
  <app-page :title="$t('nav.settings')" :subtitle="$t('settings.subtitle', { name: companyState.name })" icon="cogs">
    <template v-slot:header v-if="termsUrl">
      <b-button v-if="isSme || shouldSignDbTermsAddendum" @click="openPDF(termsUrl)" variant="primary">
        <font-awesome-icon :icon="['fas', 'file-lines']" class="mr-2" />{{ $t('settings.termsOfBusinessButton') }}
      </b-button>
      <b-button v-if="isUkCompany" @click="openPDF(subprocessorListUrl)" variant="primary">
        <font-awesome-icon :icon="['fas', 'user-shield']" class="mr-2" />{{ $t('settings.subprocessorListButton') }}
      </b-button>
      <b-button @click="openPDF(emiTermsUrl)" variant="primary">
        <font-awesome-icon :icon="['fas', 'money-bill-transfer']" class="mr-2" />{{ $t('settings.emiTermsButton') }}
      </b-button>
      <b-button @click="openPDF(emiInfoUrl)" variant="secondary">
        <font-awesome-icon :icon="['fas', 'money-bill-transfer']" class="mr-2" />{{ $t('settings.emiDutyButton') }}
      </b-button>
    </template>

    <sme-alert v-if="isUkCompany" level="info" class="mb-3">
      {{ $t('settings.subprocessorUpdatedAlert') }}
    </sme-alert>

    <sme-alert level="warning" class="mb-3">
      {{ $t('settings.changeWarningAlert') }}
    </sme-alert>

    <ValidationObserver v-slot="v" tag="div">
      <b-form @submit.prevent="v.handleSubmit(onSave)">
        <table class="table" id="company-settings-table">
          <tbody>
            <tr>
              <td>
                <div class="cell-title">
                  <h4>{{ $t('settings.tradingName.title') }}</h4>
                </div>
                <p>{{ $t('settings.tradingName.description') }}</p>
              </td>
              <td v-if="editable">
                <app-input
                  v-model="companyState.properties.sme_properties.sme_trading_name"
                  type="text"
                  name="trading name"
                  rules="required"
                  :placeholder="prettifyCompanyName(companyState.name)"
                  class="cell-value"
                ></app-input>
              </td>
              <td class="cell-value" v-if="!editable">
                {{ companyState.properties.sme_properties.sme_trading_name }}
              </td>
            </tr>

            <tr v-if="isSelfFunded">
              <td>
                <div class="cell-title">
                  <h4>{{ $t('settings.monthlyTransferLimit.title') }}</h4>
                </div>
                <p>{{ $t('settings.monthlyTransferLimit.description') }}</p>
              </td>
              <td v-if="editable">
                <app-input
                  v-model="monthlyTransferLimit"
                  type="number"
                  step="1"
                  :formatter="formatToTwoDecimals"
                  lazy-formatter
                  name="company monthly transfer limit"
                  rules="required"
                  :placeholder="$t('settings.monthlyTransferLimit.placeholder')"
                  prepend="£"
                  class="cell-value"
                ></app-input>
                <!-- £ is hardcoded here; should come from getBankingInfo() so non-GB companies get their own currency symbol. -->
              </td>
              <td class="cell-value" v-if="!editable">
                {{ toCurrency(companyState.company_monthly_transfer_limit) }}
              </td>
            </tr>

            <tr v-if="isSme">
              <td>
                <div class="cell-title">
                  <h4>{{ $t('settings.simpleDeductions.title') }}</h4>
                </div>
                <p>
                  {{ $t('settings.simpleDeductions.description') }}
                </p>
              </td>
              <td>
                <app-input
                  v-model="simpleDeductionsEnabled"
                  type="checkbox-single"
                  name="maximum reclaim"
                  class="cell-value"
                  :disabled="!editable"
                ></app-input>
              </td>
            </tr>

            <tr v-if="isEnableWhatsApp">
              <td>
                <div class="cell-title">
                  <h4>{{ $t('settings.whatsappOptIn.title') }}</h4>
                </div>
                <p>
                  {{ $t('settings.whatsappOptIn.description1') }}
                </p>
                <i18n path="settings.whatsappOptIn.description2" tag="p">
                  <template #link>
                    <a :href="whatsappContentsUrl && `/#${whatsappContentsUrl.split('#')[1]}`" target="_blank">{{
                      $t('settings.whatsappOptIn.linkText')
                    }}</a>
                  </template>
                </i18n>
              </td>
              <td>
                <app-input
                  v-model="whatsappOptInEnabled"
                  type="checkbox-single"
                  name="whatsapp opt in"
                  class="cell-value"
                  :disabled="!editable"
                ></app-input>
              </td>
            </tr>

            <tr>
              <td>
                <div class="cell-title">
                  <h4>{{ $t('settings.bankingDetails.title') }}</h4>
                </div>
                <p>
                  {{ $t('settings.bankingDetails.description') }}
                </p>
              </td>
              <td>
                <app-input
                  v-model="companySortCode"
                  type="text"
                  name="Return payment sort code"
                  class="cell-value"
                  :rules="{ regex: getBankingInfo.sortCode.regex }"
                  :placeholder="companyState.properties.beneficiary_sort_code || getBankingInfo.sortCode.placeholder"
                  :plaintext="!editable"
                  row
                >
                  <template #label>
                    {{ getBankingInfo.sortCode.label }}
                    <small v-if="editable">{{ getBankingInfo.sortCode.description }}</small>
                  </template>
                </app-input>

                <app-input
                  v-model="companyAccountNumber"
                  type="text"
                  name="Return payment account number"
                  class="cell-value"
                  :rules="{ regex: getBankingInfo.accountNumber.regex }"
                  :placeholder="
                    companyState.properties.beneficiary_account_number || getBankingInfo.accountNumber.placeholder
                  "
                  :plaintext="!editable"
                  row
                >
                  <template #label>
                    {{ getBankingInfo.accountNumber.label }}
                    <small v-if="editable">{{ getBankingInfo.accountNumber.description }}</small>
                  </template>
                </app-input>
              </td>
            </tr>

            <tr v-if="canSalarySignOff">
              <td>
                <div class="cell-title">
                  <h4>{{ $t('settings.salarySignOff.title') }}</h4>
                </div>
                <p>
                  {{ $t('settings.salarySignOff.description1') }}
                </p>
                <p>
                  {{ $t('settings.salarySignOff.description2') }}
                </p>
              </td>
              <td>
                <app-input
                  v-model="receiveSalarySignOffEmails"
                  type="checkbox-single"
                  name="salary sign-off"
                  class="cell-value"
                  :disabled="!editable"
                ></app-input>
              </td>
            </tr>
          </tbody>
        </table>

        <page-sub-footer>
          <template #before>
            <sme-alert v-if="saveError" level="danger">{{ saveError }}</sme-alert>
          </template>
          <b-button type="submit" variant="primary" :disabled="v.invalid || saving">
            <b-spinner v-if="saving" class="mr-2" small />
            {{
              saving ? $t('settings.savingButton') : editable ? $t('settings.saveButton') : $t('settings.editButton')
            }}
          </b-button>
          <b-button v-if="editable" variant="outline-primary" :disabled="saving" @click.prevent="handleCancel">
            {{ $t('settings.cancelButton') }}
          </b-button>
        </page-sub-footer>
      </b-form>
    </ValidationObserver>
  </app-page>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import defaultsDeep from 'lodash/defaultsDeep';
import { ValidationObserver } from 'vee-validate';
import ApiClient from '@/ApiClient';
import { getBankingInfo } from '@/Banking';
import tracker from '@/Tracker';
import AppInput from '@/components/AppInput.vue';
import AppPage from '@/components/AppPage';
import PageSubFooter from '@/components/PageSubFooter.vue';
import SmeAlert from '@/components/atoms/SmeAlert.vue';
import useFeatureFlags from '@/composables/useFeatureFlags';
import State from '@/state/State';
import { toCurrency } from '@/utils';
import {
  DB_SALARY_SERVICE_ADDENDUM,
  DB_TERMS_OF_BUSINESS_AMENDMENT,
  HEALTHCARE_TERMS_OF_BUSINESS_AMENDMENT,
} from '@/utils/agreements';
import { currencySymbolFromCode } from '@/utils/common';

export default {
  name: 'CompanySettings',
  components: { AppPage, ValidationObserver, AppInput, PageSubFooter, SmeAlert },
  data() {
    return {
      companyState: this.getInitialCompanyState(),
      editable: false,
      saving: false,
      saveError: undefined,
      whatsappOptInEnabled: this.getInitialWhatsappOnboardingOptIn(),
      whatsappContentsUrl: undefined,
      whatsappAgreementId: undefined,
      companySortCode: undefined,
      companyAccountNumber: undefined,
      fieldPermissions: [],
      receiveSalarySignOffEmails: undefined,
      termsUrl: '',
      subprocessorListUrl: 'https://legal.stream.co/subprocessor_list.pdf',
      emiTermsUrl: 'https://stream.co/en/modulr',
      emiInfoUrl: 'https://legal.wagestream.co.uk/Consumer+Duty+Explanatory+Note.pdf',
    };
  },
  async mounted() {
    const result = await ApiClient.smeGetWhatsAppOptIn();
    this.whatsappContentsUrl = result.data?.data?.contents_url;
    this.whatsappAgreementId = result.data?.data?.agreement_id;
    this.companySortCode = this.companyState.properties.beneficiary_sort_code;
    this.companyAccountNumber = this.companyState.properties.beneficiary_account_number;
    this.receiveSalarySignOffEmails = !this.companyState.properties.salary_sign_off_opted_out_at;
    let termsInfo;
    if (this.companyState?.properties?.[DB_SALARY_SERVICE_ADDENDUM.companyProperty]) {
      termsInfo = await ApiClient.getAgreement(DB_SALARY_SERVICE_ADDENDUM.id);
    } else if (this.companyState?.properties?.[DB_TERMS_OF_BUSINESS_AMENDMENT.companyProperty]) {
      termsInfo = await ApiClient.getAgreement(DB_TERMS_OF_BUSINESS_AMENDMENT.id);
    } else if (this.companyState?.properties?.[HEALTHCARE_TERMS_OF_BUSINESS_AMENDMENT.companyProperty]) {
      termsInfo = await ApiClient.getAgreement(HEALTHCARE_TERMS_OF_BUSINESS_AMENDMENT.id);
    } else {
      termsInfo = await ApiClient.smeGetTerms();
    }
    this.termsUrl = termsInfo?.contents_url;
  },
  setup() {
    const { isEnableWhatsApp, isSelfFunded, canSalarySignOff, isSme, isUkCompany } = useFeatureFlags();
    return {
      isEnableWhatsApp,
      isSelfFunded,
      canSalarySignOff,
      isSme,
      isUkCompany,
    };
  },
  computed: {
    getBankingInfo: () => getBankingInfo(),
    monthlyTransferLimit: {
      get() {
        return this.companyState.company_monthly_transfer_limit
          ? this.companyState.company_monthly_transfer_limit / 100
          : undefined;
      },
      set(value) {
        if (value) {
          this.companyState.company_monthly_transfer_limit = value * 100;
        } else {
          this.companyState.company_monthly_transfer_limit = undefined;
        }
      },
    },
    simpleDeductionsEnabled: {
      get() {
        return this.companyState.properties.maximum_reclaim === -1;
      },
      set(value) {
        this.companyState.properties.maximum_reclaim = value ? -1 : undefined;
      },
    },
  },

  methods: {
    toCurrency,
    currencySymbolFromCode,
    getInitialCompanyState() {
      return defaultsDeep(cloneDeep(State.state.company), {
        properties: {
          maximum_reclaim: State.state.company.properties.maximum_reclaim === -1 || undefined,
          sme_properties: {
            sme_trading_name: State.state.company.name,
          },
        },
      });
    },
    getInitialWhatsappOnboardingOptIn() {
      return State.state.company.properties.sme_properties?.whatsapp_onboarding_opt_in || false;
    },
    setInitialState() {
      this.companyState = this.getInitialCompanyState();
      this.whatsappOptInEnabled = this.getInitialWhatsappOnboardingOptIn();
    },
    prettifyCompanyName(name) {
      let lowerName = name.toLowerCase();
      if (lowerName.endsWith('ltd')) {
        lowerName = lowerName.slice(0, -3);
      }
      return lowerName.replace(/(^\w{1})|(\s+\w{1})/g, letter => letter.toUpperCase());
    },
    handleCancel() {
      this.setInitialState();

      this.editable = false;
    },
    formatToTwoDecimals(value) {
      if (Number(value)) {
        return Number(Number(value).toFixed(2));
      }
    },
    async onSave() {
      if (!this.editable) {
        this.editable = true;

        return;
      }
      const company = this.companyState;
      const propsToUpdate = {
        company_id: company.company_id,
        company_monthly_transfer_limit: company.company_monthly_transfer_limit,
        properties: {
          sme_properties: company.properties.sme_properties,
          maximum_reclaim: company.properties.maximum_reclaim,
        },
      };

      this.saveError = undefined;
      this.saving = true;

      try {
        await ApiClient.smeUpdateCompanyProperties(company.company_id, propsToUpdate);
        await this.optInWhatsapp();
        await this.toggleSalarySignOffEmails();
        await this.setCompanyBankDetails();
        await ApiClient.getUser();

        this.setInitialState();

        this.editable = false;

        this.$appToast(this.$t('settings.successToastBody', { name: company.name }), {
          title: this.$t('settings.successToastTitle'),
          variant: 'success',
        });
        tracker.trackEngagement('company_settings_changed');
      } catch (error) {
        this.saveError = error.message;
      } finally {
        this.saving = false;
      }
    },
    async optInWhatsapp() {
      const optIn = this.whatsappOptInEnabled || false;
      const agreementId = this.whatsappAgreementId;

      await ApiClient.smePostWhatsAppOptIn(optIn, State.state.user.user_id, agreementId);
    },
    async setCompanyBankDetails() {
      if (this.companySortCode && this.companyAccountNumber) {
        const payload = { sort_code: this.companySortCode, account_number: this.companyAccountNumber };
        await ApiClient.setCompanyReturnPaymentBankDetails(payload);
      }
    },
    async toggleSalarySignOffEmails() {
      const optIn = this.receiveSalarySignOffEmails || false;
      await ApiClient.toggleSalarySignOffEmails(State.state.company.company_id, optIn);
    },
    openPDF(url) {
      window.open(url, '_blank');
    },
  },
};
</script>

<style scoped lang="scss">
.edit-save {
  padding-top: 0.75rem;
  padding-bottom: 1rem;
  border-top: 1px solid var(--palette-color-default-lighten-90);
  justify-content: flex-end;

  button {
    align-self: center;
  }
}

.cell-value {
  width: 20rem;
}

.bank-details {
  display: flex;
  justify-content: space-between;
  margin: 10px 0;
}

.row-description {
  margin: 0 0.75rem;
}
</style>
